import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { from, Observable, of } from 'rxjs'
import { shareReplay, switchMap } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { CategoriesApiService } from 'src/app/builder-services/api/categories.api.service'
import { IntegrationsService } from 'src/app/integrations/integrations.service'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { environment } from 'src/environments/environment'
export interface categoriesTableColumns {
  categoryId: string
  categoryImage: string
  categoryName: string
  checked?: boolean
}
@Injectable({
  providedIn: 'root'
})
export class CategoriesService {
  availableSubs: any
  allCategories: any = []
  fetchingInProgress: boolean = false
  storedVendorCategories: any
  categorySearch: string = '' 
  filteredCategories: any[] = []
  selectedCategory: string = 'all'
  selectedCategoryName: string = 'All Categories'
  selectedSubCategoryFilter: string = ''
  private categories$?: Observable<any>
  private categoriesCachedProjectId: any = null
  get project_id() {
    return this.builderService.selectedProject
  }
  constructor(
    private builderService: BuilderService,
    private categoriesApi: CategoriesApiService,
    private http: HttpClient,
    private vendor: VendorService,
    private integrationsService: IntegrationsService
  ) {}

  storedStandaloneCategories: any

  insertCategory(cat: any, sortOrder: number): Promise<string | null> {
    return this.categoriesApi.insertCategory(this.project_id, cat.categoryName, cat.categoryImage || null, sortOrder)
  }

  updateCategory(cat: any, sortOrder: number): Promise<void> {
    return this.categoriesApi.updateCategory(String(this.project_id), cat.categoryId, cat.categoryName, cat.categoryImage || null, sortOrder)
  }

  deleteCategory(categoryId: string): Promise<void> {
    return this.categoriesApi.deleteCategory(String(this.project_id), categoryId)
  }

  insertSubCategory(sub: any, parentId: string, sortOrder: number): Promise<string | null> {
    const name = sub.subcategory || sub.subCategoryName || null
    const image = sub.categoryImage || sub.subCategoryImage || null
    return this.categoriesApi.insertSubCategory(this.project_id, parentId, name, image, sortOrder)
  }

  updateSubCategory(sub: any, sortOrder: number): Promise<void> {
    const name = sub.subcategory || sub.subCategoryName || null
    const image = sub.categoryImage || sub.subCategoryImage || null
    return this.categoriesApi.updateSubCategory(String(this.project_id), sub.subCategoryId, name, image, sortOrder)
  }

  submitCategory = (categories: any[]) => {
    categories = JSON.parse(JSON.stringify(categories))
    return new Observable<any>((observer) => {
      this.syncCategoriesWP(categories).subscribe(
        () => {},
        (error) => { console.error('Error syncing categories with WordPress:', error) }
      )
      this.allCategories = categories
      this.categories$ = undefined
      observer.next({})
      observer.complete()
    })
  }

  /**
   * Sync multiple categories with WordPress.
   *
   * This function sends an array of category objects from Firebase/Angular
   * to the custom WordPress REST API endpoint `/wp-json/v4/syncAddCategories`.
   * It performs a single POST request containing all categories in JSON format,
   * allowing efficient batch synchronization instead of multiple individual calls.
   *
   * Each category object should include the following fields:
   * {
   *   categoryId: string,         // Unique Stacks category identifier
   *   categoryName: string,       // Display name of the category
   *   categoryImage: string,      // Public URL of the category image
   *   subcategories: array        // Optional list of subcategories
   * }
   *
   * @param categories - The array of categories to sync with WordPress.
   * @returns Observable<any> - HTTP POST request observable to the WordPress endpoint.
   */
  syncCategoriesWP = (categories: any[]) => {
    return this.integrationsService.getWordpressSiteUrl().pipe(
      switchMap((url: string) => {
        if (!url) return of(null)
        const wordPressURL = environment.proxyUrl + url + '/wp-json/v4/syncAddCategories'
        return this.http.post(wordPressURL, { categories })
      })
    )
  }

  syncAllCategoriesWP = () => {
    return this.integrationsService.getWordpressSiteUrl().pipe(
      switchMap((url: string) => {
        if (!url) return of(null)
        const wordPressURL = environment.proxyUrl + url + '/wp-json/v4/syncAllCategories'
        return this.http.get(wordPressURL)
      })
    )
  }
  loadCategories = (): Observable<any[]> => {
    if (this.categories$ && this.categoriesCachedProjectId === this.project_id) {
        return this.categories$
      }
      this.categoriesCachedProjectId = this.project_id
      this.allCategories = []
      this.categories$ = new Observable<any[]>((observer) => {
        from(this.categoriesApi.getCategories(this.project_id)).subscribe({
          next: (categories) => {
            this.allCategories = categories
            this.filteredCategories = categories
            observer.next(categories)
            observer.complete()
          },
          error: (err) => observer.error(err)
        })
      }).pipe(shareReplay(1))
      return this.categories$
  }

  getCategories = () => {
    return this.loadCategories()
  }

  clearCategoriesCache = () => {
    this.categories$ = undefined
    this.allCategories = []
  }

  getVendorCats = (projectId: any) => {
    return new Observable<any>((observer) => {
      if (!this.fetchingInProgress) {
        this.fetchingInProgress = true
        from(this.categoriesApi.getCategories(projectId)).subscribe({
          next: (response) => {
            this.storedVendorCategories = response || {}
            this.fetchingInProgress = false
            observer.next(response || {})
            observer.complete()
          },
          error: (error) => {
            this.fetchingInProgress = false
            observer.error(error)
          }
        })
      }
    })
  }


  getCategory = (category_id: string) => {
    return new Observable<any>((observer) => {
      this.getCategories().subscribe({
        next: (categories: any[]) => {
          const found = categories.find((c: any) => c.categoryId === category_id);
          
          if (found) {
            observer.next(found);
          } else {
            console.log('No data available');
            observer.next({});
          }
          observer.complete();
        },
        error: (err) => observer.error(err)
      });
    });
  };

  findCategoryById = (categoriesData: any, categoryIdsToFind: string[]) => {
    const foundCategories = []
    if (categoryIdsToFind !== undefined) {
      for (let categoryId of categoryIdsToFind) {
        const foundCategory = categoriesData.find((category: { categoryId: string }) => String(category.categoryId) === String(categoryId))
        if (foundCategory) {
          foundCategories.push(foundCategory)
        }
      }
    }

    return foundCategories
  }

  findSubCategoryById = (categoriesData: any, categoryIdsToFind: string[]) => {
    const foundSubCategories = []
    if (!categoriesData) {
      return []
    }
    for (let category of categoriesData) {
      if (!categoryIdsToFind) {
        continue
      }
      for (let subCategoryId of categoryIdsToFind) {
        if (category.subcategories !== undefined) {
          const foundSubCategory = category.subcategories.find((subcategory: { subCategoryId: string }) => String(subcategory.subCategoryId) === String(subCategoryId))
          if (foundSubCategory) {
            foundSubCategories.push(foundSubCategory)
          }
        }
      }
    }

    return foundSubCategories
  }

  /**
   * Take the categories array and return array of ids
   */
  getCategoryIdFromCategories = (categories: any, type = 'category') => {
    let categoriesIds = []
    if (!categories) {
      return []
    }
    for (const category of categories) {
      if (type == 'category') {
        categoriesIds.push(category.categoryId)
      } else if (type == 'subcategory') {
        categoriesIds.push(category)
      }
    }
    return categoriesIds
  }
}
